// v49.54 — MfaChallenge.
// Sits between AuthGate and the protected shell. When the user has
// a verified TOTP factor AND their session is still at AAL1 (just
// password-signed-in), we show a full-screen challenge prompt for
// the 6-digit code. Only once verify() succeeds (session steps up
// to AAL2) does the child tree render.
//
// Renders nothing when no step-up is needed.

import { useEffect, useRef, useState } from "react";
import { LABOURCORE_API } from "@/lib/api";
import { useAuth } from "@/features/auth/AuthContext";
import { useToast } from "@/components/Toast";
import { useMfaStatus, type MfaStatus } from "@/features/auth/useMfaStatus";
import { Button } from "@/components/Button";
import { APP_VERSION } from "@/lib/version";

interface Props {
  children: React.ReactNode;
}

export function MfaGate({ children }: Props) {
  const status = useMfaStatus();
  if (status.loading) return null;
  if (!status.needsStepUp) return <>{children}</>;
  return <MfaChallengeScreen status={status} />;
}

function MfaChallengeScreen({ status }: { status: MfaStatus }) {
  const { signOut } = useAuth();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => codeInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const submit = async () => {
    if (!status.verifiedFactor || code.length !== 6 || busy) return;
    setBusy(true);
    try {
      await LABOURCORE_API.auth.mfa.challengeAndVerify({
        factorId: status.verifiedFactor.id,
        code,
      });
      /* useMfaStatus auto-refreshes on the MFA_CHALLENGE_VERIFIED
         event; that flips needsStepUp false and the children render. */
      await status.refresh();
    } catch (e) {
      toast({ kind: "warn", msg: "Code didn't match", subtitle: "Generate a fresh one from your authenticator app." });
      setCode("");
      codeInputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-lc-bg"
      style={{ fontFamily: "DM Sans, system-ui, sans-serif" }}
    >
      <div className="max-w-md w-full bg-white border border-lc-line rounded-lc-lg shadow-2xl overflow-hidden">
        <header
          className="px-6 py-5 text-white"
          style={{ background: "linear-gradient(135deg, var(--color-lc-navy), var(--color-lc-blue-dark))" }}
        >
          <div className="text-[11px] font-extrabold uppercase tracking-widest text-blue-300">Two-factor auth</div>
          <div className="text-lg font-extrabold tracking-tight mt-1">Enter your 6-digit code</div>
          <div className="text-[12px] text-blue-200 mt-1 leading-snug">
            Generate a fresh code from your authenticator app to finish signing in.
          </div>
        </header>
        <div className="px-6 py-5 flex flex-col gap-3">
          <input
            ref={codeInputRef}
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={e => { if (e.key === "Enter" && code.length === 6 && !busy) void submit(); }}
            placeholder="123456"
            className="w-full px-3 py-3 rounded-lc-md border border-lc-line text-2xl font-mono tracking-[0.4em] text-center focus:outline-none focus:border-lc-blue focus:ring-2 focus:ring-lc-blue-light"
          />
          <Button type="button" variant="primary" disabled={busy || code.length !== 6} onClick={submit}>
            {busy ? "Verifying…" : "Continue"}
          </Button>
          <button
            type="button"
            onClick={signOut}
            className="text-[12px] text-lc-mute hover:text-lc-ink-2 underline underline-offset-2 text-center"
          >
            Use a different account
          </button>
          <div className="text-[10px] text-lc-faint font-mono text-center mt-2">{APP_VERSION}</div>
        </div>
      </div>
    </div>
  );
}
